import { useEffect, useState } from "react";
import { ShieldCheck, Funnel } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import { toast } from "sonner";

const ACTION_STYLES = {
    login_success: "bg-emerald-50 text-emerald-800 border-emerald-200",
    login_failed: "bg-rose-50 text-rose-700 border-rose-200",
    logout: "bg-slate-100 text-slate-700 border-slate-300",
    user_create: "bg-emerald-50 text-emerald-800 border-emerald-200",
    user_update: "bg-amber-50 text-amber-800 border-amber-200",
    user_password_reset: "bg-amber-50 text-amber-800 border-amber-200",
    client_create: "bg-slate-100 text-slate-700 border-slate-300",
    po_create: "bg-slate-100 text-slate-700 border-slate-300",
    po_finalize: "bg-amber-50 text-amber-800 border-amber-200",
    invoice_create: "bg-emerald-50 text-emerald-800 border-emerald-200",
    payment_add: "bg-emerald-50 text-emerald-800 border-emerald-200",
    reminder_send: "bg-amber-50 text-amber-800 border-amber-200",
    document_upload: "bg-slate-100 text-slate-700 border-slate-300",
};

export default function AuditLog() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [entityTypeFilter, setEntityTypeFilter] = useState("");

    const load = async () => {
        try {
            const params = {};
            if (entityTypeFilter) params.entity_type = entityTypeFilter;
            const { data } = await api.get("/billing/audit/logs", { params });
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load audit log"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityTypeFilter]);

    const renderDiff = (log) => {
        if (!log.old_value && !log.new_value) return null;
        const keys = new Set([
            ...Object.keys(log.old_value || {}),
            ...Object.keys(log.new_value || {}),
        ]);
        const diffs = [];
        for (const k of keys) {
            const o = log.old_value?.[k];
            const n = log.new_value?.[k];
            if (JSON.stringify(o) !== JSON.stringify(n)) {
                diffs.push({ k, o, n });
            }
        }
        if (diffs.length === 0) {
            return <pre className="text-[11px] text-slate-500 font-mono whitespace-pre-wrap break-all mt-1.5">{JSON.stringify(log.new_value || log.old_value, null, 2).slice(0, 280)}</pre>;
        }
        return (
            <div className="mt-1.5 space-y-0.5">
                {diffs.slice(0, 4).map((d, i) => (
                    <div key={i} className="text-[11px] font-mono text-slate-600">
                        <span className="text-slate-400">{d.k}:</span>{" "}
                        {d.o !== undefined && <span className="line-through text-rose-600">{JSON.stringify(d.o)}</span>}
                        {" → "}
                        <span className="text-emerald-700">{JSON.stringify(d.n)}</span>
                    </div>
                ))}
                {diffs.length > 4 && <div className="text-[11px] text-slate-400">+ {diffs.length - 4} more</div>}
            </div>
        );
    };

    return (
        <div data-testid="audit-log-page">
            <PageHeader
                eyebrow="Compliance"
                title="Audit log"
                description="Every write action, login attempt and data change — tracked with actor, timestamp and old/new values."
            />
            <div className="px-6 sm:px-8 py-8 space-y-5">
                <div className="flex flex-wrap items-center gap-2" data-testid="audit-filters">
                    <div className="reedos-eyebrow flex items-center gap-1.5"><Funnel size={12} /> Filter</div>
                    <select value={entityTypeFilter} onChange={(e) => setEntityTypeFilter(e.target.value)} className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="audit-entity-filter">
                        <option value="">All entity types</option>
                        <option value="auth">Auth</option>
                        <option value="user">User</option>
                        <option value="client">Client</option>
                        <option value="po">Purchase Order</option>
                        <option value="invoice">Invoice</option>
                        <option value="payment">Payment</option>
                        <option value="document">Document</option>
                        <option value="email_template">Email template</option>
                    </select>
                </div>

                {loading ? (
                    <div className="text-sm text-slate-500">Loading audit log...</div>
                ) : items.length === 0 ? (
                    <EmptyState icon={<ShieldCheck size={40} weight="thin" />} title="No audit entries" description="No activity recorded yet for this filter." />
                ) : (
                    <div className="reedos-card overflow-hidden" data-testid="audit-log-table">
                        <div className="divide-y divide-slate-100">
                            {items.map((log) => (
                                <div key={log.id} className="px-5 py-4 flex items-start gap-4" data-testid={`audit-row-${log.id}`}>
                                    <div className="pt-0.5 w-40 shrink-0">
                                        <div className="text-[11px] text-slate-500">{formatDateTime(log.created_at)}</div>
                                        <div className="text-[11px] text-slate-400 mt-0.5 font-mono truncate">{log.extra?.ip || ""}</div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold border ${ACTION_STYLES[log.action] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
                                                {log.action}
                                            </span>
                                            <span className="text-xs text-slate-500">on</span>
                                            <span className="text-xs font-mono text-slate-700">{log.entity_type}{log.entity_id ? `:${log.entity_id}` : ""}</span>
                                        </div>
                                        <div className="text-sm text-slate-700 mt-1">
                                            <span className="font-medium">{log.actor_email}</span>
                                            <span className="text-slate-400"> · {log.actor_role}</span>
                                        </div>
                                        {renderDiff(log)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
